import { Box, CircularProgress, Typography } from '@mui/material'
import { Route, Routes } from 'react-router-dom'
import { Navigate, useLocation } from './lib/router'
import { useCapabilities, usePrincipal } from './api/hooks'
import { NavigationGuardProvider } from './components/NavigationGuard'
import { NewRun } from './screens/NewRun'
import { Results } from './screens/Results'
import { InspectPlot } from './screens/InspectPlot'
import { Regions } from './screens/Regions'
import { RegionDetail } from './screens/RegionDetail'
import { Directory } from './screens/Directory'
import { ProducerHome } from './screens/ProducerHome'
import { useEdition } from './edition'
import { VersionPage } from './screens/VersionPage'
import { useHost } from './host'
import { Surface } from './components/primitives'

export function App() {
  const { data: principal, isLoading } = usePrincipal()
  const can = useCapabilities()
  const { RootKeyApp, PlatformKeyApp, adminRoutes, sections = [] } = useEdition()
  const { pathname } = useLocation()
  const { signedOut } = useHost()

  // Unlinked and open: what is running, before any question of who you are.
  if (pathname === '/version') return <VersionPage />

  if (isLoading) {
    return (
      <Box sx={{ height: 'var(--pc-shell-height, 100vh)', display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={22} sx={{ color: 'primary.main' }} />
      </Box>
    )
  }

  // Signing in is the application around us's: it shows what it likes here.
  if (!principal?.user && !principal?.key) return <>{signedOut ?? <NotSignedIn />}</>

  // A key, not a person: a partner's root key or one of the platform's own.
  if (principal.key) return can.key ? <RootKeyApp /> : <PlatformKeyApp />

  // A supplier's or farmer's login starts from their own plots; staff of an
  // organisation without the EUDR checks start from the first section it has.
  const ownSections = sections.filter((s) => !s.feature || can.features[s.feature])
  const home = can.kind === 'supplier' ? '/farms' : can.kind === 'farmer' ? '/fields'
    : can.features.eudr || !ownSections[0]?.nav[0] ? '/checks' : ownSections[0].nav[0].to

  return (
    <NavigationGuardProvider>
      <Routes>
        <Route path="/" element={<Navigate to={home} replace />} />
        {can.kind === 'supplier' && <Route path="/farms" element={<ProducerHome kind="supplier" />} />}
        {can.kind === 'farmer' && <Route path="/fields" element={<ProducerHome kind="farmer" />} />}
        <Route path="/plots/:plotId" element={<InspectPlot />} />
        <Route path="/checks" element={<NewRun />} />
        <Route path="/checks/new" element={<NewRun />} />
        <Route path="/checks/runs/:runId" element={<Results />} />
        <Route path="/checks/runs/:runId/plots/:plotId" element={<InspectPlot />} />
        <Route path="/regions" element={<Regions />} />
        <Route path="/regions/:regionId" element={<RegionDetail />} />
        {can.kind === 'staff' && <Route path="/directory" element={<Directory />} />}
        {ownSections.flatMap((s) => s.routes(can))}
        {adminRoutes(can)}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Routes>
    </NavigationGuardProvider>
  )
}

/** Where the application around us said nothing: the token it gave was missing or refused. */
function NotSignedIn() {
  return (
    <Box sx={{ height: 'var(--pc-shell-height, 100vh)', display: 'grid', placeItems: 'center', p: 6 }}>
      <Surface sx={{ maxWidth: 440, p: 8 }}>
        <Typography variant="h3" sx={{ mb: 2 }}>Not signed in</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          The API did not accept a user here: the token is missing, expired or revoked.
        </Typography>
      </Surface>
    </Box>
  )
}

